// FILE: src/pages/ApprovalManager/ApprovalManagerDashboard.js
//
// WHAT CHANGED vs YOUR CURRENT FILE:
// ─────────────────────────────────────────────────────────────────
// ORIGINAL: showed stat cards + mapped employees list. No selfies.
//
// NEW: added "Today's Attendance Photos" section
//   - Fetches GET /api/attendance/today-selfies
//   - Shows each employee: name, clock-in time, distance from branch
//   - Shows selfie thumbnail (click to view full screen)
//   - Shows clock-out selfie thumbnail if employee already clocked out
//   - Uses AuthenticatedImage so the JWT token is sent with image requests
//     (plain <img src="..."> would fail with 401 on the protected endpoint)
//   - No approve/reject here — purely visual identity verification
//   - Works for on-time AND late employees equally
//
// All original stat cards + mapped employees sections UNCHANGED.
// ─────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Button,
    CircularProgress, Chip, Stack, Avatar, Divider, Alert,
    Dialog, DialogContent, IconButton, Tooltip, Paper
} from '@mui/material';
import {
    CalendarMonth, AccessTimeFilled, EventNote,
    FlashOn, Person, CheckCircle, CameraAlt,
    Close, LocationOn, AccessTime
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import AuthenticatedImage from '../../components/AuthenticatedImage';

/* ── Stat Card (unchanged) ── */
const StatCard = ({ title, count, icon, color, path, navigate }) => (
    <Card
        sx={{
            borderRadius: 3, border: `1px solid ${color}30`, cursor: 'pointer',
            transition: 'transform 0.15s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
        }}
        onClick={() => navigate(path)}
    >
        <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h3" fontWeight={900} color={color}>{count}</Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>{title}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${color}20`, color, width: 52, height: 52 }}>{icon}</Avatar>
            </Stack>
            <Button size="small" variant="outlined"
                sx={{ mt: 2, borderColor: color, color, borderRadius: 2, fontWeight: 700 }}
                onClick={e => { e.stopPropagation(); navigate(path); }}>
                Review →
            </Button>
        </CardContent>
    </Card>
);

/* ── Employee Selfie Card (NEW) ── */
const SelfieCard = ({ record, onViewSelfie }) => {
    const name = record.employeeName || '-';
    const clockIn = record.clockInTime ? record.clockInTime.substring(0, 5) : null;
    const clockOut = record.clockOutTime ? record.clockOutTime.substring(0, 5) : null;
    const distance = record.clockInDistanceMeters != null
        ? `${Math.round(record.clockInDistanceMeters)}m from branch` : null;
    const isLate = record.late || record.lateMinutes > 0;

    const clockInPath = record.clockInSelfieUrl
        ? `/attendance/selfie/${record.clockInSelfieUrl}` : null;
    const clockOutPath = record.clockOutSelfieUrl
        ? `/attendance/selfie/${record.clockOutSelfieUrl}` : null;

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">

                {/* Clock-in selfie thumbnail */}
                <AuthenticatedImage
                    src={clockInPath}
                    alt={`${name} clock-in`}
                    size={64}
                    onClick={() => clockInPath && onViewSelfie(clockInPath)}
                    sx={{
                        borderRadius: 2, flexShrink: 0,
                        border: clockInPath ? '2px solid #16a34a' : '2px dashed #cbd5e1'
                    }}
                />

                {/* Details */}
                <Box flex={1} minWidth={0}>
                    <Typography fontWeight={700} fontSize={14} noWrap>{name}</Typography>

                    {clockIn && (
                        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.3}>
                            <AccessTime sx={{ fontSize: 13, color: '#64748b' }} />
                            <Typography fontSize={12} color="text.secondary">
                                In: {clockIn}{clockOut && ` · Out: ${clockOut}`}
                            </Typography>
                        </Stack>
                    )}

                    {distance && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LocationOn sx={{ fontSize: 13, color: '#64748b' }} />
                            <Typography fontSize={12} color="text.secondary">{distance}</Typography>
                        </Stack>
                    )}

                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                        {isLate && (
                            <Chip label="Late" size="small" color="warning"
                                sx={{ fontSize: 10, height: 18, fontWeight: 700 }} />
                        )}
                        {clockInPath && (
                            <Chip label="📸 Click to verify" size="small"
                                sx={{ fontSize: 10, height: 18, bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600 }} />
                        )}
                        {!clockIn && (
                            <Chip label="Not clocked in" size="small" color="default"
                                sx={{ fontSize: 10, height: 18 }} />
                        )}
                    </Stack>
                </Box>

                {/* Clock-out selfie (smaller thumbnail) */}
                {clockOutPath && (
                    <Tooltip title="Clock-out selfie">
                        <Box>
                            <AuthenticatedImage
                                src={clockOutPath}
                                alt="clock-out"
                                size={40}
                                onClick={() => onViewSelfie(clockOutPath)}
                                sx={{ borderRadius: 1.5, border: '2px solid #0284c7', flexShrink: 0 }}
                            />
                        </Box>
                    </Tooltip>
                )}
            </Stack>
        </Paper>
    );
};

/* ── MAIN COMPONENT ── */
const ApprovalManagerDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // original state
    const [counts, setCounts] = useState({ leave: 0, attendance: 0, missedPunch: 0, sudden: 0 });
    const [mappedEmployees, setMappedEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    // new state for selfies
    const [todaySelfies, setTodaySelfies] = useState([]);
    const [selfiesLoading, setSelfiesLoading] = useState(true);
    const [viewSelfieUrl, setViewSelfieUrl] = useState(''); // full-screen preview

    const fetchData = useCallback(async () => {
        setLoading(true);
        setSelfiesLoading(true);
        try {
            const [leave, att, mp, sudden, employees, selfies] = await Promise.all([
                api.get('/leaves/pending/approval-manager').catch(() => ({ data: [] })),
                api.get('/attendance/pending-approval-manager').catch(() => ({ data: [] })),
                api.get('/missed-punch/approval-manager').catch(() => ({ data: [] })),
                api.get('/sudden-requests/approval-manager').catch(() => ({ data: [] })),
                api.get(`/employees/approval-manager/${user?.employeeId}/employees`).catch(() => ({ data: [] })),
                api.get('/attendance/today-selfies').catch(() => ({ data: [] })), // NEW
            ]);
            setCounts({
                leave:       (leave.data || []).length,
                attendance:  (att.data   || []).length,
                missedPunch: (mp.data    || []).length,
                sudden:      (sudden.data || []).length,
            });
            setMappedEmployees(employees.data || []);
            setTodaySelfies(selfies.data || []); // NEW
        } catch (e) {
            console.error('Dashboard fetch error:', e);
        } finally {
            setLoading(false);
            setSelfiesLoading(false);
        }
    }, [user?.employeeId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const totalPending = counts.leave + counts.attendance + counts.missedPunch + counts.sudden;

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress size={60} />
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>

            {/* Header (unchanged) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={1}>
                <Box>
                    <Typography variant="h4" fontWeight={900} color="#1e293b">
                        Approval Manager Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        First-level approvals for your mapped employees
                    </Typography>
                </Box>
                {totalPending > 0
                    ? <Chip label={`${totalPending} Pending`} color="error" sx={{ fontWeight: 800, fontSize: 14, px: 1 }} />
                    : <Chip icon={<CheckCircle />} label="All Clear" color="success" sx={{ fontWeight: 700 }} />
                }
            </Stack>

            {totalPending === 0 && (
                <Alert severity="success" variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
                    Great! No pending approvals. All requests from your employees are processed.
                </Alert>
            )}

            {/* Stat Cards (unchanged) */}
            <Grid container spacing={3} mb={4}>
                {[
                    { title: 'Leave Requests',  count: counts.leave,       icon: <CalendarMonth />,    color: '#7c3aed', path: '/approval-manager/leave-approvals' },
                    { title: 'Late Attendance', count: counts.attendance,  icon: <AccessTimeFilled />, color: '#dc2626', path: '/approval-manager/attendance-approvals' },
                    { title: 'Missed Punch',    count: counts.missedPunch, icon: <EventNote />,        color: '#d97706', path: '/approval-manager/missed-punch' },
                    { title: 'Sudden Requests', count: counts.sudden,      icon: <FlashOn />,          color: '#0284c7', path: '/approval-manager/sudden-approvals' },
                ].map(card => (
                    <Grid item xs={12} sm={6} md={3} key={card.title}>
                        <StatCard {...card} navigate={navigate} />
                    </Grid>
                ))}
            </Grid>

            {/* ── NEW: TODAY'S ATTENDANCE PHOTOS ── */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CameraAlt color="action" />
                            <Typography variant="h6" fontWeight={700}>Today's Attendance Photos</Typography>
                        </Stack>
                        <Chip
                            label={`${todaySelfies.filter(r => r.clockInSelfieUrl).length} photos`}
                            size="small" variant="outlined"
                        />
                    </Stack>
                    <Typography fontSize={12} color="text.secondary" mb={2}>
                        Click any photo to verify it's really your employee. Selfies are captured at clock-in and clock-out.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {selfiesLoading ? (
                        <CircularProgress size={30} />
                    ) : todaySelfies.length === 0 ? (
                        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                            None of your employees have clocked in today yet.
                        </Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {todaySelfies.map(record => (
                                <Grid item xs={12} sm={6} md={4} key={record.id || record.employeeId}>
                                    <SelfieCard record={record} onViewSelfie={url => setViewSelfieUrl(url)} />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* Mapped Employees (unchanged) */}
            <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                        <Person color="action" />
                        <Typography variant="h6" fontWeight={700}>
                            My Employees ({mappedEmployees.length})
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    {mappedEmployees.length === 0 ? (
                        <Alert severity="info">No employees mapped to you yet. Contact HR to map employees.</Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {mappedEmployees.map(emp => (
                                <Grid item xs={12} sm={6} md={4} key={emp.id}>
                                    <Box sx={{
                                        p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0',
                                        display: 'flex', alignItems: 'center', gap: 1.5
                                    }}>
                                        <Avatar sx={{ bgcolor: '#0f172a', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>
                                            {emp.name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" fontWeight={700}>{emp.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {emp.designation || emp.role} · {emp.branchName || ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* Full-screen selfie viewer */}
            <Dialog open={!!viewSelfieUrl} onClose={() => setViewSelfieUrl('')} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#0f172a' }}>
                    <IconButton
                        onClick={() => setViewSelfieUrl('')}
                        sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}
                    >
                        <Close />
                    </IconButton>
                    {viewSelfieUrl && (
                        <AuthenticatedImage
                            src={viewSelfieUrl}
                            size="100%"
                            sx={{ width: '100%', height: 'auto', maxHeight: '80vh', borderRadius: 0 }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ApprovalManagerDashboard;
